// Verbatim port of the constants that back the categorization + UI.
// Do not refactor the regex in SUBCATEGORIES or the tier rules in categorize.ts
// without first running the golden-file tests. They encode production bug fixes.

import type { Category, Impact, PostType, Topic, UpdateType } from './types';

// The Dataverse data only changes once a day at 08:00 Finnish time (05:00 UTC
// in summer, 06:00 UTC in winter) when the RSS-fetch flow runs, so 5-minute
// polls were ~100% redundant cache hits AND visibly twitched the UI. 1 hour is plenty to pick up the daily refresh if the tab
// stays open across the schedule. The manual refresh button is always there
// for users who want it sooner.
export const REFRESH_INTERVAL_MS = 3_600_000; // 1 hour

export const COLORS: {
  category: Record<Category | 'other', string>;
  impact: Record<Impact, string>;
} = {
  category: {
    foundry: '#0078D4',
    copilot: '#6264A7',
    ai: '#9B59B6',
    'azure-ai': '#00A4EF',
    modernwork: '#008272',
    cowork: '#E8590C',
    security: '#D13438',
    partner: '#107C10',
    other: '#8B8B8B',
  },
  impact: {
    critical: '#D13438',
    high: '#F7630C',
    medium: '#FFC83D',
    low: '#60B0E0',
  },
};

export type CategoryOption = { value: Category | 'Kaikki'; color: string; label: string };

export const CATEGORIES: CategoryOption[] = [
  { value: 'Kaikki', color: '#444', label: 'Kaikki' },
  { value: 'foundry', color: '#0078D4', label: 'Foundry' },
  { value: 'ai', color: '#9B59B6', label: 'AI' },
  { value: 'copilot', color: '#6264A7', label: 'Copilot' },
  { value: 'cowork', color: '#E8590C', label: 'Cowork' },
  { value: 'modernwork', color: '#008272', label: 'Modern Work' },
  { value: 'azure-ai', color: '#00A4EF', label: 'Azure' },
  { value: 'security', color: '#D13438', label: 'Security' },
  { value: 'partner', color: '#107C10', label: 'Partner' },
];

export const TYPE_LABELS: Record<UpdateType, string> = {
  ga: 'LAUNCHED',
  deprecation: 'RETIREMENT',
  preview: 'IN PREVIEW',
  development: 'IN DEVELOPMENT',
  rollout: 'ROLLING OUT',
  update: 'UPDATE',
  announcement: 'ANNOUNCEMENT',
  pricing: 'PRICING',
  security: 'SECURITY',
};

export const TYPE_COLORS_BG: Record<UpdateType, string> = {
  ga: '#DFF6DD',
  deprecation: '#FDE7E9',
  preview: '#EBF3FC',
  development: '#FFF4CE',
  rollout: '#E8F5E9',
  update: '#F3F2F1',
  announcement: '#F3F2F1',
  pricing: '#F3F2F1',
  security: '#FDE7E9',
};

export const TYPE_COLORS_FG: Record<UpdateType, string> = {
  ga: '#107C10',
  deprecation: '#A80000',
  preview: '#0078D4',
  development: '#6D5700',
  rollout: '#2E7D32',
  update: '#605E5C',
  announcement: '#605E5C',
  pricing: '#605E5C',
  security: '#A80000',
};

// Secondary "Microsoft update type" badges applied on top of the primary type.
export const MS_UPDATE_TYPES: { pattern: RegExp; label: string }[] = [
  { pattern: /\bfeatures?\b/i, label: 'Features' },
  { pattern: /regions? & datacenters?|regions?.*datacenters?/i, label: 'Regions' },
  { pattern: /pricing|offerings/i, label: 'Pricing' },
  { pattern: /compliance/i, label: 'Compliance' },
  { pattern: /open source/i, label: 'Open Source' },
  { pattern: /sdk|tools/i, label: 'SDK & Tools' },
  { pattern: /\bmigration?\b/i, label: 'Migration' },
  { pattern: /microsoft ignite/i, label: 'Ignite' },
  { pattern: /microsoft build/i, label: 'Build' },
];

export type SubcategoryDef = { value: string; label: string; pattern: RegExp };

export const SUBCATEGORIES: Partial<Record<Category, SubcategoryDef[]>> = {
  foundry: [
    { value: 'agent-service', label: 'Agent Service', pattern: /agent service|hosted agent/i },
    { value: 'foundry-local', label: 'Foundry Local', pattern: /foundry local|on.device|on.premise/i },
    { value: 'sdk-api', label: 'SDK & API', pattern: /sdk|api|rest |cli /i },
    {
      value: 'models',
      label: 'Mallit',
      pattern: /gpt-|claude|phi-|mistral|grok|nemotron|model catalog|model serving/i,
    },
    { value: 'labs', label: 'Labs & Preview', pattern: /lab|experiment|preview/i },
  ],
  ai: [
    {
      value: 'llm-models',
      label: 'LLM-mallit',
      pattern:
        /gpt-|claude|gemini|llama|phi-|mistral|grok|nemotron|model catalog|model serving|large language|\bllm\b/i,
    },
    {
      value: 'azure-ai-services',
      label: 'Azure AI Services',
      pattern:
        /azure ai|ai service|cognitive|document intelligence|speech|translator|text analytics|language understanding/i,
    },
    {
      value: 'mcp-agents',
      label: 'MCP & Agentit',
      pattern:
        /\bmcp\b|model context protocol|ai agent|agent framework|supervisor agent|sre agent/i,
    },
    {
      value: 'ml-datascience',
      label: 'ML & Data Science',
      pattern: /machine learning|azure ml|deep learning|neural|databricks|training|fine.tun/i,
    },
    {
      value: 'ai-tools',
      label: 'AI-työkalut',
      pattern:
        /ai builder|ai search|semantic kernel|langchain|prompt flow|vector search|embeddings|rag\b/i,
    },
    {
      value: 'responsible-ai',
      label: 'Responsible AI',
      pattern: /responsible ai|content safety|guardrail|safety/i,
    },
  ],
  copilot: [
    {
      value: 'copilot-chat',
      label: 'Copilot Chat',
      pattern: /copilot chat|copilot app|\bchat\b.*copilot/i,
    },
    {
      value: 'copilot-studio',
      label: 'Copilot Studio',
      pattern: /copilot studio|declarative agent|agent builder/i,
    },
    {
      value: 'copilot-m365',
      label: 'M365 Copilot',
      pattern:
        /copilot in (teams|outlook|word|excel|powerpoint|onenote|loop)|microsoft 365 copilot|copilot.*notebook|copilot.*page/i,
    },
    {
      value: 'github-copilot',
      label: 'GitHub Copilot',
      pattern: /github copilot|copilot in (ssms|visual studio|vs code)/i,
    },
    {
      value: 'copilot-security',
      label: 'Copilot for Security',
      pattern: /copilot.*(security|defender|sentinel|purview)|security.*copilot/i,
    },
    {
      value: 'copilot-governance',
      label: 'Hallinta & DLP',
      pattern:
        /copilot.*(dlp|governance|compliance|admin|license|policy)|dlp.*copilot|purview.*copilot/i,
    },
  ],
  modernwork: [
    { value: 'teams', label: 'Teams', pattern: /\bteams\b/i },
    { value: 'outlook', label: 'Outlook', pattern: /\boutlook\b/i },
    { value: 'sharepoint', label: 'SharePoint', pattern: /\bsharepoint\b/i },
    { value: 'viva', label: 'Viva', pattern: /\bviva\b/i },
    { value: 'onedrive', label: 'OneDrive', pattern: /\bonedrive\b/i },
    {
      value: 'planner-loop',
      label: 'Planner & Loop',
      pattern: /\bplanner\b|\bloop\b|\bwhiteboard\b/i,
    },
    { value: 'other-m365', label: 'Muu M365', pattern: /bookings|clipchamp|stream|sway|office/i },
  ],
  'azure-ai': [
    {
      value: 'compute',
      label: 'Compute & VM',
      pattern: /virtual machine|\bvm\b|compute|batch|vmware|function|app service|confidential/i,
    },
    {
      value: 'storage',
      label: 'Storage',
      pattern: /storage|blob|files|disk|data box|netapp|data lake/i,
    },
    {
      value: 'networking',
      label: 'Networking',
      pattern:
        /network|load balancer|front door|dns|nat gateway|vpn|virtual network|private link|cdn/i,
    },
    {
      value: 'databases',
      label: 'Databases',
      pattern: /database|postgresql|mysql|sql |cosmos db|redis/i,
    },
    {
      value: 'containers',
      label: 'Containers & K8s',
      pattern: /kubernetes|aks|container|openshift|service fabric|fleet manager/i,
    },
    {
      value: 'integration',
      label: 'Integration',
      pattern: /event grid|service bus|api management|logic app|event hub/i,
    },
    {
      value: 'monitoring',
      label: 'Monitoring & Mgmt',
      pattern: /monitor|grafana|log analytics|policy|resource manager|backup|migrate/i,
    },
    {
      value: 'regions',
      label: 'Alueet & DC',
      pattern: /region|datacenter|availability zone|extended zone/i,
    },
  ],
  security: [
    {
      value: 'entra',
      label: 'Entra ID',
      pattern: /\bentra\b|identity|conditional access|\bmfa\b|authentication|passwordless/i,
    },
    {
      value: 'defender',
      label: 'Defender & Sentinel',
      pattern: /defender|sentinel|threat|security operation/i,
    },
    {
      value: 'purview-dlp',
      label: 'Purview & DLP',
      pattern: /purview|dlp|data loss|information protection|insider risk|compliance/i,
    },
    {
      value: 'network-security',
      label: 'Verkkoturvallisuus',
      pattern: /firewall|bastion|waf|web application firewall|network security|nsg|perimeter/i,
    },
    {
      value: 'encryption',
      label: 'Salaus & Key Vault',
      pattern: /key vault|encrypt|certificate|tls|ssl|tde /i,
    },
  ],
  partner: [
    {
      value: 'program',
      label: 'Ohjelma',
      pattern: /partner program|partner center|mpn|incentive/i,
    },
    {
      value: 'technical',
      label: 'Tekninen',
      pattern: /technical|workshop|enablement|training/i,
    },
    {
      value: 'business',
      label: 'Liiketoiminta',
      pattern: /business|revenue|market|customer|sales/i,
    },
  ],
};

// Priority order for the Kanban view.
export const CATEGORY_KANBAN_ORDER: Category[] = [
  'foundry',
  'ai',
  'copilot',
  'cowork',
  'modernwork',
  'azure-ai',
  'security',
  'partner',
];

export const IMPACT_ORDER: Record<Impact, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};

export const IMPACT_LABELS: Record<Impact, string> = {
  critical: 'Kriittinen',
  high: 'Korkea',
  medium: 'Keskitaso',
  low: 'Matala',
};

// Yhteisö tab: community post topic and post-type badges.
export const TOPIC_LABELS: Record<Topic, string> = {
  copilot: 'Copilot',
  'copilot-studio': 'Copilot Studio ja agentit',
  purview: 'Purview ja tietosuoja',
  security: 'Tietoturva ja identiteetti',
  'sharepoint-teams': 'SharePoint ja Teams',
  'power-platform': 'Power Platform',
  'azure-ai': 'Azure ja AI Foundry',
  devices: 'Laitteet ja hallinta',
  other: 'Muut',
};

export const POST_TYPE_LABELS: Record<PostType, string> = {
  'how-to': 'Ohje',
  'deep-dive': 'Syväsukellus',
  opinion: 'Näkemys',
  event: 'Tapahtuma',
  news: 'Uutinen',
};
